<template>
  <div>
    <v-form @submit.prevent="submitResponse">
      <p class="mb-5">
        Consider the following scenario. A system of 6 flipped coins has a total of 64 different
        microstates. The number of microstates corresponding to each distribution (number of heads
        and tails flipped) are tabulated below:
      </p>

      <v-simple-table style="width: 450px" class="mb-5 pl-10">
        <thead>
          <tr>
            <th style="vertical-align: middle; font-size: 13px">
              <stemble-latex content="$\text{Distribution}$" />
            </th>
            <th style="vertical-align: middle; text-align: center; font-size: 13px">
              <stemble-latex content="$\text{Heads}$" />
            </th>
            <th style="vertical-align: middle; text-align: center; font-size: 13px">
              <stemble-latex content="$\text{Tails}$" />
            </th>
            <th style="vertical-align: middle; text-align: center; font-size: 13px">
              <stemble-latex content="$\text{Microstates}$" />
            </th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <th style="vertical-align: middle; font-size: 13px">
              <stemble-latex content="$\text{1}$" />
            </th>
            <th style="vertical-align: middle; text-align: center; font-size: 13px">
              <stemble-latex content="$\text{6}$" />
            </th>
            <th style="vertical-align: middle; text-align: center; font-size: 13px">
              <stemble-latex content="$\text{0}$" />
            </th>
            <th style="vertical-align: middle; text-align: center; font-size: 13px">
              <stemble-latex content="$\text{1}$" />
            </th>
          </tr>
          <tr>
            <th style="vertical-align: middle; font-size: 13px">
              <stemble-latex content="$\text{2}$" />
            </th>
            <th style="vertical-align: middle; text-align: center; font-size: 13px">
              <stemble-latex content="$\text{5}$" />
            </th>
            <th style="vertical-align: middle; text-align: center; font-size: 13px">
              <stemble-latex content="$\text{1}$" />
            </th>
            <th style="vertical-align: middle; text-align: center; font-size: 13px">
              <stemble-latex content="$\text{6}$" />
            </th>
          </tr>
          <tr>
            <th style="vertical-align: middle; font-size: 13px">
              <stemble-latex content="$\text{3}$" />
            </th>
            <th style="vertical-align: middle; text-align: center; font-size: 13px">
              <stemble-latex content="$\text{4}$" />
            </th>
            <th style="vertical-align: middle; text-align: center; font-size: 13px">
              <stemble-latex content="$\text{2}$" />
            </th>
            <th style="vertical-align: middle; text-align: center; font-size: 13px">
              <stemble-latex content="$\text{15}$" />
            </th>
          </tr>
          <tr>
            <th style="vertical-align: middle; font-size: 13px">
              <stemble-latex content="$\text{4}$" />
            </th>
            <th style="vertical-align: middle; text-align: center; font-size: 13px">
              <stemble-latex content="$\text{3}$" />
            </th>
            <th style="vertical-align: middle; text-align: center; font-size: 13px">
              <stemble-latex content="$\text{3}$" />
            </th>
            <th style="vertical-align: middle; text-align: center; font-size: 13px">
              <stemble-latex content="$\text{20}$" />
            </th>
          </tr>
          <tr>
            <th style="vertical-align: middle; font-size: 13px">
              <stemble-latex content="$\text{5}$" />
            </th>
            <th style="vertical-align: middle; text-align: center; font-size: 13px">
              <stemble-latex content="$\text{2}$" />
            </th>
            <th style="vertical-align: middle; text-align: center; font-size: 13px">
              <stemble-latex content="$\text{4}$" />
            </th>
            <th style="vertical-align: middle; text-align: center; font-size: 13px">
              <stemble-latex content="$\text{15}$" />
            </th>
          </tr>
          <tr>
            <th style="vertical-align: middle; font-size: 13px">
              <stemble-latex content="$\text{6}$" />
            </th>
            <th style="vertical-align: middle; text-align: center; font-size: 13px">
              <stemble-latex content="$\text{1}$" />
            </th>
            <th style="vertical-align: middle; text-align: center; font-size: 13px">
              <stemble-latex content="$\text{5}$" />
            </th>
            <th style="vertical-align: middle; text-align: center; font-size: 13px">
              <stemble-latex content="$\text{6}$" />
            </th>
          </tr>
          <tr>
            <th style="vertical-align: middle; font-size: 13px">
              <stemble-latex content="$\text{7}$" />
            </th>
            <th style="vertical-align: middle; text-align: center; font-size: 13px">
              <stemble-latex content="$\text{0}$" />
            </th>
            <th style="vertical-align: middle; text-align: center; font-size: 13px">
              <stemble-latex content="$\text{6}$" />
            </th>
            <th style="vertical-align: middle; text-align: center; font-size: 13px">
              <stemble-latex content="$\text{1}$" />
            </th>
          </tr>
        </tbody>
      </v-simple-table>

      <p class="mb-2">a) Which distribution has the <b>highest</b> entropy associated with it?</p>

      <v-col class="d-flex mt-0 mb-0 pb-0" cols="4" sm="4">
        <v-select v-model="inputs.distribution" :items="items" label="Distribution" outlined />
      </v-col>

      <p class="mb-3">
        b) Determine the change in entropy when the sample goes from
        <b>distribution <number-value :value="initialState" /></b>
        to
        <b>distribution <number-value :value="finalState" />.</b>
      </p>

      <calculation-input
        v-model="inputs.deltaS"
        class="mb-2"
        prepend-text="$\Delta\text{S}:$"
        append-text="$\text{J/K}$"
        :disabled="!allowEditing"
      />
    </v-form>
  </div>
</template>

<script>
import DynamicQuestionMixin from '../../mixins/dynamic-question';
import CalculationInput from '../inputs/CalculationInput';
import NumberValue from '../NumberValue';
import StembleLatex from '@/tasks/components/StembleLatex';

export default {
  name: 'Question315',
  components: {
    CalculationInput,
    NumberValue,
    StembleLatex,
  },
  mixins: [DynamicQuestionMixin()],
  data() {
    return {
      // Put a list of inputs or computed properties here to be sent for marking
      inputs: {
        distribution: null,
        deltaS: null,
      },
      items: ['1', '2', '3', '4', '5', '6', '7'],
    };
  },
  computed: {
    initialState() {
      return this.taskState.getValueBySymbol('initialState').content;
    },
    finalState() {
      return this.taskState.getValueBySymbol('finalState').content;
    },
  },
};
</script>
